import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, FileText, Briefcase, ArrowRight, Users, Plus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { StatCard } from "@/components/ui/StatCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import { formatAOA } from "@/lib/utils";
import pt from "@/lib/i18n";

const STAGE_ORDER = ["lead", "qualificado", "proposta", "negociacao", "fechado_ganho", "fechado_perdido"];
const STAGE_COLOR = {
    lead: "#94A3B8",
    qualificado: "#2D6A8A",
    proposta: "#E8A96A",
    negociacao: "#C87941",
    fechado_ganho: "#2D7A4F",
    fechado_perdido: "#C0392B",
};
const STAGE_VARIANT = {
    lead: "neutral",
    qualificado: "info",
    proposta: "warning",
    negociacao: "warning",
    fechado_ganho: "success",
    fechado_perdido: "error",
};

const SalesOverview = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [deals, setDeals] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [s, d] = await Promise.all([apiGet("/deals/stats"), apiGet("/deals")]);
                setStats(s);
                setDeals(d || []);
            } catch (e) { setDeals([]); }
        })();
    }, []);

    const funnelData = STAGE_ORDER.map((s) => ({
        stage: pt.sales.stages[s],
        key: s,
        count: stats?.by_stage?.[s]?.count || 0,
        value: stats?.by_stage?.[s]?.value_aoa || 0,
    }));

    return (
        <div data-testid="sales-overview-page" className="space-y-6">
            <PageHeader
                title={pt.sales.title}
                subtitle={pt.sales.subtitle}
                testId="sales-header"
                right={
                    <>
                        <NxButton variant="secondary" onClick={() => navigate("/vendas/propostas")} icon={<FileText size={14} />} data-testid="btn-view-proposals">
                            {pt.sales.viewProposals}
                        </NxButton>
                        <NxButton variant="copper" onClick={() => navigate("/vendas/copiloto")} icon={<Sparkles size={14} />} data-testid="btn-open-copilot">
                            {pt.sales.copilot}
                        </NxButton>
                    </>
                }
            />

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    label={pt.sales.kpiPipeline}
                    value={stats?.total_pipeline_aoa ?? 0}
                    format="currency_aoa"
                    index={0}
                    testId="kpi-pipeline"
                />
                <StatCard label={pt.sales.kpiLeads} value={stats?.leads_this_month ?? 0} index={1} testId="kpi-leads" />
                <StatCard label={pt.sales.kpiConversion} value={stats?.conversion_rate_pct ?? 0} format="percent" index={2} testId="kpi-conversion" />
                <StatCard label={pt.sales.kpiProposals} value={stats?.proposals_sent ?? 0} index={3} testId="kpi-proposals" />
            </div>

            {/* Funnel chart */}
            <GlassCard padding="p-6" data-testid="funnel-card">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{pt.sales.funnelTitle}</h3>
                    <NxBadge variant="copper">{(deals || []).length} negócios</NxBadge>
                </div>
                <div style={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                        <BarChart data={funnelData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                            <XAxis dataKey="stage" tick={{ fill: "var(--slate-mid)", fontSize: 11 }} stroke="var(--glass-border)" />
                            <YAxis tick={{ fill: "var(--slate-mid)", fontSize: 11 }} stroke="var(--glass-border)" />
                            <Tooltip
                                contentStyle={{ background: "rgba(255,247,237,0.96)", border: "1px solid var(--copper-subtle)", borderRadius: 8, fontFamily: "DM Sans, sans-serif" }}
                                formatter={(v, name, props) => [`${v} negócios`, "Quantidade"]}
                                labelStyle={{ color: "var(--slate-deep)" }}
                            />
                            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                {funnelData.map((entry, idx) => (
                                    <Cell key={idx} fill={STAGE_COLOR[entry.key]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </GlassCard>

            {/* Deals table */}
            <GlassCard padding="p-0" className="overflow-hidden" data-testid="deals-card">
                <div className="p-4 border-b border-[color:var(--glass-border)] flex items-center justify-between">
                    <h3 className="font-display text-xl text-[color:var(--slate-deep)]">{pt.sales.dealsTitle}</h3>
                </div>
                {deals === null ? (
                    <div className="p-4 space-y-2">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12" />)}</div>
                ) : deals.length === 0 ? (
                    <p className="p-8 text-center font-body text-sm text-[color:var(--slate-mid)]">{pt.common.noDeals}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="deals-table">
                            <thead className="bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] uppercase text-[10px] tracking-wider">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">{pt.sales.col_deal}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.sales.col_company}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.sales.col_stage}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.sales.col_value}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.sales.col_owner}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.sales.col_close}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.sales.col_days}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deals.slice(0, 25).map((d, i) => (
                                    <tr key={d.id} className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--copper-subtle)]/40 transition-colors" data-testid={`deal-row-${d.id}`}>
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-[color:var(--copper-dark)]">{d.deal_number}</td>
                                        <td className="px-4 py-2.5 font-body text-[color:var(--slate-deep)]">{d.company_name}</td>
                                        <td className="px-4 py-2.5"><NxBadge variant={STAGE_VARIANT[d.stage]}>{pt.sales.stages[d.stage]}</NxBadge></td>
                                        <td className="px-4 py-2.5 text-right font-mono text-[color:var(--slate-deep)]">{d.estimated_value_aoa ? `AOA ${formatAOA(d.estimated_value_aoa)}` : "—"}</td>
                                        <td className="px-4 py-2.5 text-[color:var(--slate-mid)]">{d.owner?.full_name || "—"}</td>
                                        <td className="px-4 py-2.5 text-[color:var(--slate-mid)] font-mono text-xs">{d.close_date || "—"}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-xs text-[color:var(--slate-mid)]">{d.days_in_stage}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>
        </div>
    );
};

export default SalesOverview;
